import { useState } from "react";
import { styled } from "styled-components";

import { ListItemBox } from "common/components";
import { breakpoints } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { CaseStudySection } from "domains/caseStudy/components/CaseStudySection";
import { TopicCustomContentSection } from "domains/customContentAndSources/components/TopicCustomContentSection";
import { EventSection } from "domains/events/components/EventSection";
import { InitiativesSection } from "domains/initiatives/components/InitiativesSection";
import { KnowledgeDataSection } from "domains/knowledgeData/components/KnowledgeDataSection";
import { PinnedKnowledgeSection } from "domains/pinAddReject/components/PinnedKnowledgeSection";
import { PublicationSection } from "domains/publication/components/PublicationSection";
import { StakeholdersSection } from "domains/stakeholders/components/StakeholdersSection";
import {
  getAreThereKnowledgeItemFilters,
  getAreThereKnowledgeItems,
  getAreThereKnowledgeItemsPending,
} from "domains/topicDetail/selectors";
import { getTmSelectedId } from "domains/transformationMap/selectors";
import { VideoSection } from "domains/videos/components/VideoSection";
import {
  getCanAccessTopicCustomContent,
  getCanAccessTopicEvents,
  getCanAccessTopicInitiatives,
  getCanAccessTopicStakeholders,
} from "entities/permissions/selectors";

const StyledCard = styled(ListItemBox)`
  padding: 1rem;

  margin: 0 1rem;

  @media ${breakpoints.medium} {
    margin: 0 2rem;
  }

  @media ${breakpoints.large} {
    margin: 0;
    margin-right: 2rem;
  }

  @media ${breakpoints.extraLarge} {
    margin-right: 3rem;
  }
`;

type BrowseKnowledgeFeedProps = { topicId: string };

export const BrowseKnowledgeFeed = ({ topicId }: BrowseKnowledgeFeedProps) => {
  const translate = useTranslate();
  const accessToEvents = useSelector(getCanAccessTopicEvents);
  const accessToInitiatives = useSelector(getCanAccessTopicInitiatives);
  const accessToStakeholders = useSelector(getCanAccessTopicStakeholders);
  const accessToCustomContent = useSelector(getCanAccessTopicCustomContent);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const id = selectedInnerNodeId ?? topicId;
  const areThereKnowledgeItems = useSelector(getAreThereKnowledgeItems(id));
  const areThereKnowledgeItemsPending = useSelector(getAreThereKnowledgeItemsPending(id));
  const areThereKnowledgeItemFilters = useSelector(getAreThereKnowledgeItemFilters);
  const [selectedSection, setSelectedSection] = useState<string | undefined>();

  const selectSectionHandle = (id: string) => setSelectedSection(!selectedSection ? id : undefined);

  return (
    <>
      {accessToCustomContent && (
        <TopicCustomContentSection
          topicId={topicId}
          selectedSectionId={selectedSection}
          selectSectionHandle={selectSectionHandle}
        />
      )}
      <PinnedKnowledgeSection
        topicId={topicId}
        selectedSectionId={selectedSection}
        selectSectionHandle={selectSectionHandle}
      />
      <PublicationSection
        topicId={topicId}
        selectedSectionId={selectedSection}
        selectSectionHandle={selectSectionHandle}
      />
      <CaseStudySection
        topicId={topicId}
        selectedSectionId={selectedSection}
        selectSectionHandle={selectSectionHandle}
      />
      <VideoSection topicId={topicId} selectedSectionId={selectedSection} selectSectionHandle={selectSectionHandle} />
      <KnowledgeDataSection
        topicId={topicId}
        selectedSectionId={selectedSection}
        selectSectionHandle={selectSectionHandle}
      />
      {accessToEvents && <EventSection topicId={topicId} selectedSectionId={selectedSection} />}
      {accessToInitiatives && <InitiativesSection topicId={topicId} selectedSectionId={selectedSection} />}
      {accessToStakeholders && <StakeholdersSection topicId={topicId} selectedSectionId={selectedSection} />}
      {!areThereKnowledgeItems && !areThereKnowledgeItemsPending && !areThereKnowledgeItemFilters && (
        <StyledCard>{translate("app.discover.topic.tabs.knowledge.no_data")}</StyledCard>
      )}
    </>
  );
};
